import type { QueryKey } from "@tanstack/react-query";
import type { ShouldRevalidateFunction } from "react-router-dom";
import * as APITypes from "~/API";
import { queryClient } from "~/lib/queryClient";
import { getCachedAuthAccessInfo } from "~/utils/auth/authContextCache";

/** Tier A — property, building, unit, amenity, unit plan (5–10 min). */
export const STALE_TIME_TIER_A = 5 * 60 * 1000;

/** Tier B — tenants (2–5 min). */
export const STALE_TIME_TIER_B = 3 * 60 * 1000;

/** Tier C — leases, finance (1–2 min). */
export const STALE_TIME_TIER_C = 2 * 60 * 1000;

/** Tier D — transactions (1 min). */
export const STALE_TIME_TIER_D = 60 * 1000;

type OwnerScope = {
  ownerID: string;
  ownerType: APITypes.OwnerType;
};

export function normalizeSearchParams(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const entries = Array.from(params.entries())
    .filter(([, value]) => value !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(entries).toString();
}

export function isQueryFresh(queryKey: QueryKey, staleTime: number): boolean {
  const state = queryClient.getQueryState(queryKey);
  if (state?.dataUpdatedAt == null) {
    return false;
  }
  if (state.isInvalidated) {
    return false;
  }
  return Date.now() - state.dataUpdatedAt < staleTime;
}

export const CACHED_ENTITY_KINDS = [
  "property",
  "building",
  "unit",
  "parcel",
  "amenity",
  "unit-plan",
  "lease",
  "transaction",
  "serviceRequest",
] as const;

export type CachedEntityKind = (typeof CACHED_ENTITY_KINDS)[number];

export type EntityCacheInvalidationTarget = {
  entity: CachedEntityKind;
  entityID?: string;
};

export function entityDetailsQueryKey(
  entity: CachedEntityKind,
  entityID: string,
): readonly [string, string] {
  return [`${entity}-details`, entityID];
}

/** Invalidate table list cache and optional details cache for an entity. */
export function invalidateEntityLoaderCaches(
  entity: CachedEntityKind,
  entityID?: string,
): void {
  invalidateLoaderQueries([`${entity}-table`]);
  if (entityID) {
    invalidateLoaderQueries(entityDetailsQueryKey(entity, entityID));
  }
}

export function invalidateEntityCaches(
  targets: EntityCacheInvalidationTarget[],
): void {
  for (const { entity, entityID } of targets) {
    invalidateEntityLoaderCaches(entity, entityID);
  }
}

/** Run a mutation and always bust related loader caches (pass or fail). */
export async function withEntityCacheInvalidation<T>(
  targets: EntityCacheInvalidationTarget[],
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn();
  } finally {
    invalidateEntityCaches(targets);
  }
}

export function setLoaderQueryData<T>(
  queryKey: QueryKey,
  data: T,
  staleTime: number,
): void {
  queryClient.setQueryData(queryKey, data);
  queryClient.setQueryDefaults(queryKey, { staleTime });
}

export function invalidateLoaderQueries(prefix: QueryKey): void {
  queryClient.invalidateQueries({ queryKey: prefix });
}

function resolveOwnerScope(): OwnerScope | null {
  return getCachedAuthAccessInfo();
}

/** Table pages with client-side filters — skip loader on same-path query changes. */
export function createClientFilteredShouldRevalidate(
  getQueryKey: (owner: OwnerScope) => QueryKey,
  staleTime: number,
): ShouldRevalidateFunction {
  return ({ currentUrl, nextUrl, defaultShouldRevalidate, formMethod }) => {
    if (formMethod && formMethod !== "GET") {
      return defaultShouldRevalidate;
    }

    if (currentUrl.pathname === nextUrl.pathname) {
      return false;
    }

    const owner = resolveOwnerScope();
    if (owner && isQueryFresh(getQueryKey(owner), staleTime)) {
      return false;
    }

    return defaultShouldRevalidate;
  };
}

/** Table pages with server-side filters — revalidate when search changes unless cached. */
export function createServerFilteredShouldRevalidate(
  getQueryKey: (owner: OwnerScope, url: URL) => QueryKey,
  staleTime: number,
): ShouldRevalidateFunction {
  return ({ currentUrl, nextUrl, defaultShouldRevalidate, formMethod }) => {
    if (formMethod && formMethod !== "GET") {
      return defaultShouldRevalidate;
    }

    const owner = resolveOwnerScope();
    if (owner && isQueryFresh(getQueryKey(owner, nextUrl), staleTime)) {
      return false;
    }

    return defaultShouldRevalidate;
  };
}
